import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import './TenantDetail.css';
import { FaKey } from 'react-icons/fa';

// Exemplo de dados para desenvolvimento - será substituído por dados da API
const mockTenant = {
  id: '1',
  name: 'Empresa ABC',
  email: 'contato@abc.com',
  phone: '(11) 99999-8888',
  document: '12.345.678/0001-90',
  address: 'Rua das Flores, 123 - Centro, São Paulo/SP',
  credits: 250.50,
  active: true,
  createdAt: '2023-10-15T14:30:00Z',
  updatedAt: '2023-12-18T09:15:00Z',
  transactions: [
    { id: 'tx1', type: 'credit', amount: 100, date: '2023-12-10T11:30:00Z', description: 'Adição de créditos' },
    { id: 'tx2', type: 'debit', amount: 2.5, date: '2023-12-12T14:45:00Z', description: 'Consulta GetCID' },
    { id: 'tx3', type: 'debit', amount: 1.5, date: '2023-12-15T10:20:00Z', description: 'Verificação PidKey' },
    { id: 'tx4', type: 'credit', amount: 50, date: '2023-12-17T16:30:00Z', description: 'Adição de créditos' },
  ]
};

interface Transaction {
  id: string;
  type: 'credit' | 'debit';
  amount: number;
  date: string;
  description: string;
}

interface User {
  id: string;
  username: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
}

interface QueryType {
  id: string;
  name: string;
  description?: string;
  defaultPrice: number;
  active: boolean;
}

interface TenantPrice {
  id: string;
  tenantId: string;
  queryTypeId: string;
  price: number;
  queryType: QueryType;
}

interface Tenant {
  id: string;
  name: string;
  email: string;
  phone: string;
  document: string;
  address: string;
  credits: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  transactions?: Transaction[];
  users?: User[];
  prices?: TenantPrice[];
}

interface EditableTenantData {
  name: string;
  email: string;
  phone: string;
  document: string;
  address: string;
  active: boolean;
}

const TenantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [addCreditAmount, setAddCreditAmount] = useState<string>('');
  const [isAddingCredit, setIsAddingCredit] = useState<boolean>(false);
  const [creditDescription, setCreditDescription] = useState<string>('');
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editData, setEditData] = useState<EditableTenantData>({
    name: '',
    email: '',
    phone: '',
    document: '',
    address: '',
    active: true
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    // console.log('TenantDetail mounted, params id:', id);
    
    if (id && id.trim() !== '') {
      // console.log('ID válido encontrado:', id);
      fetchTenantData();
    } else {
      console.error('ID inválido ou não fornecido:', id);
      setError('ID do cliente não fornecido');
      setLoading(false);
    }
  }, [id]);

  const fetchTenantData = async () => {
    // console.log('Iniciando fetchTenantData com ID:', id);
    
    if (!id) {
      console.error('ID não fornecido para fetchTenantData');
      setError('ID do cliente não fornecido');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Chamada real à API usando nosso serviço
      const { getTenantById } = await import('../../../services/api');
      // console.log('Chamando getTenantById com ID:', id);
      const tenantResponse = await getTenantById(id);
      
      // console.log('Resposta da API tenant:', tenantResponse);
      
      if (tenantResponse.success && tenantResponse.tenant) {
        // console.log('Tenant encontrado:', tenantResponse.tenant);
        
        // Agora vamos buscar o histórico de transações do tenant
        const { getTenantTransactions } = await import('../../../services/api');
        // console.log('Buscando histórico de transações para o tenant:', id);
        const transactionsResponse = await getTenantTransactions(id);
        
        // console.log('Resposta do histórico de transações:', transactionsResponse);
        
        // Processar as transações do histórico de recargas
        let transactions: Transaction[] = [];
        
        if (transactionsResponse.success && transactionsResponse.data) {
          // Transformar as recargas em transações
          transactions = transactionsResponse.data.recharges.map((recharge: any) => ({
            id: recharge.id,
            type: 'credit',
            amount: recharge.amount,
            date: recharge.createdAt,
            description: recharge.description || 'Adição de créditos'
          }));
          
          // Se houver histórico de requisições com débitos, adicionar aqui também
          if (transactionsResponse.data.requests) {
            const debitTransactions = transactionsResponse.data.requests.map((request: any) => ({
              id: request.id,
              type: 'debit',
              amount: request.creditsSpent,
              date: request.createdAt,
              description: `Consulta ${request.queryType?.name || 'API'}`
            }));
            
            transactions = [...transactions, ...debitTransactions];
            
            // Ordenar as transações por data, mais recentes primeiro
            transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          }
        }
        
        // Garantir que tenant tenha a estrutura completa, adicionando transactions vazias se não existir
        const completeData = {
          ...tenantResponse.tenant,
          transactions: transactions, // Usar as transações buscadas
          // Adicionando outros campos que podem não vir da API mas são usados no frontend
          document: tenantResponse.tenant.document || '',
          address: tenantResponse.tenant.address || ''
        };
        
        setTenant(completeData);
        initializeEditData(completeData);
      } else {
        console.error('Falha ao obter tenant:', tenantResponse.message || 'Cliente não encontrado');
        setError(tenantResponse.message || 'Cliente não encontrado');
      }
    } catch (error: any) {
      console.error('Erro ao buscar tenant:', error);
      setError(error.message || 'Erro ao carregar dados do cliente. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const initializeEditData = (tenantData: Tenant) => {
    setEditData({
      name: tenantData.name,
      email: tenantData.email,
      phone: tenantData.phone || '',
      document: tenantData.document || '',
      address: tenantData.address || '',
      active: tenantData.active
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleToggleStatus = () => {
    if (!tenant) return;
    
    // Aqui seria uma chamada à API para atualizar o status
    const newStatus = !tenant.active;
    
    setIsUpdating(true);
    
    // Chamada real à API
    import('../../../services/api').then(({ updateTenant }) => {
      updateTenant(tenant.id, { active: newStatus })
        .then(response => {
          if (response.success && response.data.success) {
            setTenant({
              ...tenant,
              active: newStatus,
              updatedAt: response.data.tenant?.updatedAt || new Date().toISOString()
            });
            setSuccessMessage(`Cliente ${newStatus ? 'ativado' : 'desativado'} com sucesso!`);
            setTimeout(() => setSuccessMessage(null), 3000);
          } else {
            setError(response.error || response.data?.message || 'Erro ao atualizar status');
          }
        })
        .catch(error => {
          setError('Erro ao atualizar status do cliente');
          console.error(error);
        })
        .finally(() => {
          setIsUpdating(false);
        });
    });
  };

  const handleAddCredit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenant) return;
    
    const amount = parseFloat(addCreditAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Por favor, insira um valor válido');
      return;
    }
    
    setIsAddingCredit(true);
    setError(null);
    
    // API real
    import('../../../services/api').then(({ addCredits }) => {
      addCredits(tenant.id, {
        amount,
        description: creditDescription || 'Adição de créditos'
      })
        .then(response => {
          if (response.success) {
            // Após adicionar créditos com sucesso, buscar o histórico completo atualizado
            fetchUpdatedData(response.data.tenant);
            
            // Limpar o formulário
            setAddCreditAmount('');
            setCreditDescription('');
          } else {
            setError(response.error || 'Erro ao adicionar créditos');
            setIsAddingCredit(false);
          }
        })
        .catch(error => {
          setError('Erro ao adicionar créditos');
          console.error(error);
          setIsAddingCredit(false);
        });
    });
  };

  // Função auxiliar para buscar dados atualizados após adicionar créditos
  const fetchUpdatedData = async (updatedTenantData: any) => {
    try {
      // Buscar o histórico de transações atualizado
      const { getTenantTransactions } = await import('../../../services/api');
      const transactionsResponse = await getTenantTransactions(tenant.id);
      
      if (transactionsResponse.success && transactionsResponse.data) {
        // console.log('Histórico de transações atualizado:', transactionsResponse.data);
        
        // Processar as transações
        let transactions: Transaction[] = [];
        
        // Transformar as recargas em transações
        transactions = transactionsResponse.data.recharges.map((recharge: any) => ({
          id: recharge.id,
          type: 'credit',
          amount: recharge.amount,
          date: recharge.createdAt,
          description: recharge.description || 'Adição de créditos'
        }));
        
        // Se houver histórico de requisições com débitos, adicionar aqui também
        if (transactionsResponse.data.requests) {
          const debitTransactions = transactionsResponse.data.requests.map((request: any) => ({
            id: request.id,
            type: 'debit',
            amount: request.creditsSpent,
            date: request.createdAt,
            description: `Consulta ${request.queryType?.name || 'API'}`
          }));
          
          transactions = [...transactions, ...debitTransactions];
          
          // Ordenar as transações por data, mais recentes primeiro
          transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        }
        
        // Atualizar o estado do tenant com os dados atualizados, incluindo as transações
        setTenant({
          ...tenant,
          credits: updatedTenantData.credits,
          updatedAt: updatedTenantData.updatedAt,
          transactions: transactions
        });
      }
    } catch (error) {
      console.error('Erro ao buscar dados atualizados:', error);
    } finally {
      setIsAddingCredit(false);
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setEditData(prev => ({ ...prev, [name]: checked }));
    } else {
      setEditData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenant) return;
    
    setIsSaving(true);
    setError(null);
    
    // API real
    import('../../../services/api').then(({ updateTenant }) => {
      updateTenant(tenant.id, editData)
        .then(response => {
          if (response.success && response.data.success) {
            // console.log('Tenant atualizado com sucesso:', response.data.tenant);
            
            // Atualizar o state com os dados atualizados
            setTenant({
              ...tenant,
              ...response.data.tenant,
              // Garantir que os campos existentes sejam preservados se não retornados
              transactions: tenant.transactions,
              users: tenant.users,
              prices: tenant.prices
            });
            
            setEditMode(false);
            
            // Mostrar mensagem de sucesso temporária
            setError(null);
            setIsSaving(false);
          } else {
            console.error('Falha ao atualizar tenant:', response.error || response.data?.message || 'Erro desconhecido');
            setError(response.error || response.data?.message || 'Erro ao atualizar dados do cliente');
            setIsSaving(false);
          }
        })
        .catch(error => {
          console.error('Erro na solicitação:', error);
          setError('Erro de conexão. Tente novamente.');
          setIsSaving(false);
        });
    });
  };

  const handleCancelEdit = () => {
    if (tenant) {
      initializeEditData(tenant);
    }
    setEditMode(false);
  };

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleSavePassword = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenant) return;
    
    if (passwordData.password !== passwordData.confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return;
    }
    
    if (passwordData.password.length < 6) {
      setPasswordError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setIsSavingPassword(true);
    setPasswordError(null);
    
    import('../../../services/api').then(({ updateTenantPassword }) => {
      updateTenantPassword(tenant.id, passwordData)
        .then(response => {
          if (response.success && response.data?.success) {
            // console.log('Senha do tenant atualizada com sucesso');
            
            // Limpar o formulário e fechá-lo
            setPasswordData({
              password: '',
              confirmPassword: ''
            });
            setShowPasswordForm(false);
            
            // Mostrar mensagem de sucesso temporária
            setError(null);
            setSuccessMessage('Senha atualizada com sucesso!');
            setTimeout(() => setSuccessMessage(null), 3000);
          } else {
            console.error('Falha ao atualizar senha:', response.error || response.data?.message || 'Erro desconhecido');
            
            // Verificar se é o erro específico de "não foram encontrados usuários administradores"
            if (response.data?.message?.includes('Não foram encontrados usuários administradores')) {
              setPasswordError('Criando usuário administrador automaticamente com o email do cliente...');
              
              // Tentar novamente após um pequeno delay
              setTimeout(() => {
                updateTenantPassword(tenant.id, passwordData)
                  .then(retryResponse => {
                    if (retryResponse.success && retryResponse.data?.success) {
                      setPasswordData({
                        password: '',
                        confirmPassword: ''
                      });
                      setShowPasswordForm(false);
                      setPasswordError(null);
                      setSuccessMessage('Usuário administrador criado e senha definida com sucesso!');
                      setTimeout(() => setSuccessMessage(null), 3000);
                    } else {
                      setPasswordError(retryResponse.error || retryResponse.data?.message || 'Erro ao criar usuário administrador');
                    }
                  })
                  .finally(() => {
                    setIsSavingPassword(false);
                  });
              }, 1000);
            } else {
              setPasswordError(response.error || response.data?.message || 'Erro ao atualizar senha');
              setIsSavingPassword(false);
            }
          }
        })
        .catch(error => {
          console.error('Erro na solicitação:', error);
          setPasswordError('Erro de conexão. Tente novamente.');
          setIsSavingPassword(false);
        });
    });
  };

  // Método para ativar/desativar usuários
  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const { toggleUserStatus } = await import('../../../services/api');
      const response = await toggleUserStatus(userId, !currentStatus);
      
      if (response.success) {
        // Atualizar a lista de usuários no tenant
        await fetchTenantData();
        setSuccessMessage(`Usuário ${!currentStatus ? 'ativado' : 'desativado'} com sucesso`);
        
        // Limpar a mensagem após 3 segundos
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        setError(response.error || 'Erro ao alterar status do usuário');
      }
    } catch (error) {
      setError('Erro ao processar a solicitação');
      console.error('Erro ao alterar status do usuário:', error);
    }
  };

  if (loading) {
    return <div className="loading">Carregando detalhes do cliente...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <Link to="/dashboard/admin/tenants" className="btn-back">
          &larr; Voltar para a lista de clientes
        </Link>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="error-container">
        <p className="error-message">Cliente não encontrado</p>
        <Link to="/dashboard/admin/tenants" className="btn-back">
          &larr; Voltar para a lista de clientes
        </Link>
      </div>
    );
  }

  return (
    <div className="tenant-detail-container">
      <div className="detail-header">
        <div>
          <Link to="/dashboard/admin/tenants" className="btn-back">
            &larr; Voltar para a lista de clientes
          </Link>
          <h2>{tenant.name}</h2>
        </div>
        <div className="detail-actions">
          <button
            className="btn-primary"
            onClick={() => setShowPasswordForm(true)}
          >
            Alterar Senha
          </button>
          <button
            className={`btn-toggle-status ${tenant.active ? 'deactivate' : 'activate'}`}
            onClick={handleToggleStatus}
            disabled={isUpdating}
          >
            {tenant.active ? 'Desativar' : 'Ativar'} Cliente
          </button>
          <button
            className="btn-edit"
            onClick={() => setEditMode(true)}
          >
            Editar Cliente
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}
      
      {editMode ? (
        <div className="edit-form-container">
          <form onSubmit={handleSaveEdit} className="tenant-edit-form">
            <h3>Editar Informações do Cliente</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">Nome da Empresa*</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={editData.name}
                  onChange={handleEditInputChange}
                  required
                  disabled={isSaving}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email*</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={editData.email}
                  onChange={handleEditInputChange}
                  required
                  disabled={isSaving}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Telefone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={editData.phone}
                  onChange={handleEditInputChange}
                  disabled={isSaving}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="document">CNPJ</label>
                <input
                  type="text"
                  id="document"
                  name="document"
                  value={editData.document}
                  onChange={handleEditInputChange}
                  disabled={isSaving}
                />
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="address">Endereço</label>
                <textarea
                  id="address"
                  name="address"
                  value={editData.address}
                  onChange={handleEditInputChange}
                  disabled={isSaving}
                />
              </div>
              
              <div className="form-group checkbox-group">
                <label htmlFor="active">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={editData.active}
                    onChange={handleEditInputChange}
                    disabled={isSaving}
                  />
                  Cliente Ativo
                </label>
              </div>
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={isSaving}
              >
                {isSaving ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="tenant-info-grid">
          <div className="info-card">
            <h3>Informações Gerais</h3>
            <div className="info-item">
              <span className="label">Status:</span>
              <span className={`status-badge ${tenant.active ? 'active' : 'inactive'}`}>
                {tenant.active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">E-mail:</span>
              <span>{tenant.email}</span>
            </div>
            <div className="info-item">
              <span className="label">Telefone:</span>
              <span>{tenant.phone || 'Não informado'}</span>
            </div>
            <div className="info-item">
              <span className="label">CNPJ:</span>
              <span>{tenant.document || 'Não informado'}</span>
              <button 
                className="btn-icon-small"
                onClick={() => setShowPasswordForm(true)}
                title="Alterar Senha"
              >
                <FaKey />
              </button>
            </div>
            <div className="info-item">
              <span className="label">Endereço:</span>
              <span>{tenant.address || 'Não informado'}</span>
            </div>
            <div className="info-item">
              <span className="label">Data de Cadastro:</span>
              <span>{formatDate(tenant.createdAt)}</span>
            </div>
            <div className="info-item">
              <span className="label">Última Atualização:</span>
              <span>{formatDate(tenant.updatedAt)}</span>
            </div>
          </div>

          <div className="info-card">
            <h3>Gerenciamento de Créditos</h3>
            <div className="credits-display">
              <div className="current-credits">
                <span className="label">Saldo Atual:</span>
                <span className="amount">{tenant.credits.toFixed(2)}</span>
              </div>
              
              <form onSubmit={handleAddCredit} className="add-credits-form">
                <h4>Adicionar Créditos</h4>
                <div className="form-row">
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={addCreditAmount}
                    onChange={(e) => setAddCreditAmount(e.target.value)}
                    placeholder="Valor"
                    required
                    disabled={isAddingCredit}
                  />
                </div>
                <div className="form-row">
                  <input
                    type="text"
                    value={creditDescription}
                    onChange={(e) => setCreditDescription(e.target.value)}
                    placeholder="Descrição (opcional)"
                    disabled={isAddingCredit}
                  />
                </div>
                <div className="form-row">
                  <button 
                    type="submit" 
                    className="btn-submit-credits"
                    disabled={isAddingCredit}
                  >
                    {isAddingCredit ? 'Adicionando...' : 'Adicionar Créditos'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="transactions-section">
        <h3>Histórico de Transações</h3>
        {tenant.transactions && tenant.transactions.length === 0 ? (
          <p className="no-transactions">Nenhuma transação encontrada.</p>
        ) : tenant.transactions && tenant.transactions.length > 0 ? (
          <div className="transactions-table-container">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Tipo</th>
                  <th>Valor</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                {tenant.transactions.map(transaction => (
                  <tr key={transaction.id} className={transaction.type === 'debit' ? 'debit-row' : 'credit-row'}>
                    <td>{formatDate(transaction.date)}</td>
                    <td>
                      <span className={`transaction-type ${transaction.type}`}>
                        {transaction.type === 'credit' ? 'Crédito' : 'Débito'}
                      </span>
                    </td>
                    <td className="amount-cell">{transaction.amount.toFixed(2)}</td>
                    <td>{transaction.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-transactions">Nenhuma transação encontrada.</p>
        )}
      </div>

      <div className="users-section">
        <h3>Usuários do Cliente</h3>
        {tenant.users && tenant.users.length === 0 ? (
          <p className="no-users">Nenhum usuário encontrado.</p>
        ) : tenant.users && tenant.users.length > 0 ? (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Função</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {tenant.users.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{user.role === 'admin' ? 'Administrador' : 'Cliente'}</td>
                    <td>
                      <span className={`status-badge ${user.active ? 'active' : 'inactive'}`}>
                        {user.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button 
                        className={`btn-toggle-status ${user.active ? 'deactivate' : 'activate'}`}
                        onClick={() => handleToggleUserStatus(user.id, user.active)}
                      >
                        {user.active ? 'Desativar' : 'Ativar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-users">Nenhum usuário encontrado.</p>
        )}
      </div>

      <div className="prices-section">
        <h3>Preços de Consultas</h3>
        {tenant.prices && tenant.prices.length === 0 ? (
          <p className="no-prices">Nenhum preço configurado.</p>
        ) : tenant.prices && tenant.prices.length > 0 ? (
          <div className="prices-table-container">
            <table className="prices-table">
              <thead>
                <tr>
                  <th>Tipo de Consulta</th>
                  <th>Descrição</th>
                  <th>Preço</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {tenant.prices.map(price => (
                  <tr key={price.id}>
                    <td>{price.queryType.name}</td>
                    <td>{price.queryType.description || '—'}</td>
                    <td className="price-cell">{price.price.toFixed(2)}</td>
                    <td>
                      <button 
                        className="btn-edit-price"
                        onClick={() => {
                          const newPrice = prompt(`Novo preço para ${price.queryType.name}:`, price.price.toString());
                          if (newPrice !== null) {
                            const numPrice = parseFloat(newPrice);
                            if (!isNaN(numPrice) && numPrice >= 0) {
                              // Importar dinamicamente para evitar problemas de ciclo de dependência
                              import('../../../services/api').then(({ updateTenantPrice }) => {
                                updateTenantPrice(tenant.id, price.queryType.id, numPrice)
                                  .then(response => {
                                    if (response.success) {
                                      // Atualizar preço na interface
                                      const updatedPrices = tenant.prices.map(p => 
                                        p.id === price.id ? {...p, price: numPrice} : p
                                      );
                                      setTenant({...tenant, prices: updatedPrices});
                                      alert('Preço atualizado com sucesso!');
                                    } else {
                                      alert(`Erro ao atualizar preço: ${response.error || 'Erro desconhecido'}`);
                                    }
                                  })
                                  .catch(err => {
                                    console.error('Erro ao atualizar preço:', err);
                                    alert('Erro ao conectar com o servidor. Tente novamente.');
                                  });
                              });
                            } else {
                              alert('Por favor, insira um valor numérico não negativo.');
                            }
                          }
                        }}
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-prices">Nenhum preço configurado.</p>
        )}
      </div>

      <div className="tenant-status">
        <div className={`status-indicator ${tenant.active ? 'active' : 'inactive'}`}>
          {tenant.active ? 'Ativo' : 'Inativo'}
        </div>
        <div className="tenant-actions">
          {/* <button 
            className="config-button"
            onClick={() => navigate(`/admin/tenants/${tenant.id}/price-config`)}
          >
            Configurar Preços
          </button>*/}
        </div>
      </div>

      <div className="actions-button-group">
        {/* <button 
          className={`btn-secondary ${!editMode ? 'disabled' : ''}`}
          onClick={() => setEditMode(true)}
          disabled={!editMode || isSaving}
        >
          Editar
        </button>*/}
      </div>

      {showPasswordForm && (
        <div className="modal-overlay">
          <div className="password-form-container">
            <form onSubmit={handleSavePassword} className="password-form">
              <h3>Alterar Senha do Cliente</h3>
              
              <div className="form-group">
                <label htmlFor="password">Nova Senha*</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={passwordData.password}
                  onChange={handlePasswordInputChange}
                  required
                  disabled={isSavingPassword}
                  placeholder="******"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar Senha*</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordInputChange}
                  required
                  disabled={isSavingPassword}
                  placeholder="******"
                />
              </div>
              
              {passwordError && <div className="form-error">{passwordError}</div>}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ password: '', confirmPassword: '' });
                    setPasswordError(null);
                  }}
                  disabled={isSavingPassword}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn-save"
                  disabled={isSavingPassword}
                >
                  {isSavingPassword ? 'Salvando...' : 'Salvar Senha'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="action-buttons">
        {editMode ? (
          <>
            <button 
              className="primary-button" 
              onClick={handleSaveEdit} 
              disabled={isSaving}
            >
              {isSaving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
            <button 
              className="secondary-button" 
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              Cancelar
            </button>
          </>
        ) : (
          <>
            {/* Botões que foram comentados temporariamente
            <button 
              className="edit-button" 
              onClick={() => setEditMode(true)}
            >
              Editar
            </button>
            <button 
              className={tenant.active ? "warning-button" : "success-button"} 
              onClick={handleToggleStatus}
              disabled={isUpdating}
            >
              {isUpdating ? 'Atualizando...' : tenant.active ? 'Desativar' : 'Ativar'}
            </button>
            <button 
              className="key-button" 
              onClick={() => setShowPasswordForm(true)}
            >
              <FaKey /> Definir Senha
            </button>
            <button 
              className="primary-button"
              onClick={() => navigate(`/admin/tenants/${tenant.id}/price-config`)}
            >
              Configurar Preços
            </button>
            */}
          </>
        )}
      </div>
    </div>
  );
};

export default TenantDetail; 